'use client';

import { SignUp, useAuth, useClerk } from '@clerk/clerk-react';
import { shadcn } from '@clerk/themes';
import {
  CardElement,
  Elements,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { createRoute, useNavigate } from '@tanstack/react-router';
import { Suspense, useEffect, useState } from 'react';

import { Footer } from '@/components/layouts/Footer';
import { OnboardingNavigation } from '@/components/layouts/OnboardingNavigation';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Input,
  Skeleton,
} from '@/components/ui';
import { ThemeToggle } from '@/components/ui/ThemeToggle';
import { Toaster } from '@/components/ui/Toaster';
import { useToast } from '@/hooks/useToast';
import { GITHUB_APP_INSTALL_URL } from '@/lib/constants';
import { normalizeInstallationSearch } from '@/lib/onboarding/installationSearch';
import { setActiveOrganization } from '@/lib/organization/switchActiveOrganization';
import {
  OnboardingError,
  useCompleteOnboarding,
} from '@/lib/query/useCompleteOnboarding';
import { useOnboardingStatus } from '@/lib/query/useOnboardingStatus';
import {
  isHealthy,
  useOrganizationsWithHealth,
} from '@/lib/query/useOrganizationsWithHealth';
import { usePricing } from '@/lib/query/usePricing';
import { useSubscription } from '@/lib/query/useSubscription';
import { seoHead } from '@/lib/seo/routeMeta';
import { theme } from '@/lib/ui/theme';

import { Route as rootRoute } from './__root';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY!);

const TRIAL_DAYS = 7;

// Canonical seat price in cents, mirroring pricing.tsx's $30 display. Used as a
// fallback so the onboarding card never renders a blank price when the pricing API
// is slow or returns null; the live tier price from usePricing() overrides it.
const DEFAULT_SEAT_PRICE_CENTS = 3000;

// What a new customer is signing up for — sourced from the FAQ + pricing copy,
// kept terse for the onboarding card's left column.
const VALUE_POINTS: string[] = [
  'Deterministic reviews: the same code yields the same findings every run, with nothing hallucinated.',
  '75+ checks across correctness, architecture, reliability, security, concurrency, and performance.',
  'Go, Python, TypeScript, and Rust: every check, every language.',
  'A stop hook auto-fix loop for Claude Code, Codex, Kimi Code, and Antigravity CLI, plus GitHub PR check runs and on-demand CLI reviews.',
  'Disagree with a finding? Silence one check, recorded and reviewable, never silent.',
];

// formatTrialEnd renders the ISO trial-end date as a friendly "until <date>" so
// the customer knows exactly when (and whether) their card is first charged.
function formatTrialEnd(
  trialEndedAt: string | null | undefined
): string | null {
  if (!trialEndedAt) return null;
  const d = new Date(trialEndedAt);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding',
  head: () => seoHead('/onboarding'),
  validateSearch: normalizeInstallationSearch,
  component: OnboardingPage,
});

function PaymentForm({
  onSuccess,
  onError,
  couponCode,
  onCouponCodeChange,
}: {
  onSuccess: (paymentMethodId: string) => void;
  onError: (error: string) => void;
  couponCode: string;
  onCouponCodeChange: (code: string) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    onError('');

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      onError('Card element not found');
      setProcessing(false);
      return;
    }

    const { paymentMethod, error } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
    });

    if (error) {
      onError(error.message || 'Failed to save payment method');
      setProcessing(false);
    } else if (paymentMethod?.id) {
      setProcessing(false);
      onSuccess(paymentMethod.id);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="mb-2 block text-sm font-medium">Coupon Code</label>
        <Input
          type="text"
          placeholder="Enter coupon code (optional)"
          value={couponCode}
          onChange={(e) => onCouponCodeChange(e.target.value)}
        />
      </div>
      <div className="border-border rounded-md border p-3">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#fff',
                '::placeholder': { color: '#aab7c4' },
              },
            },
          }}
        />
      </div>
      <Button type="submit" disabled={!stripe || processing} className="w-full">
        {processing ? 'Processing...' : 'Save Payment Method'}
      </Button>
    </form>
  );
}

function OnboardingContent() {
  const { isSignedIn, isLoaded } = useAuth();
  const navigate = useNavigate();
  const { installation_id: installationIdFromUrl } = Route.useSearch();

  const { data: onboarding, isLoading: isLoadingOnboarding } =
    useOnboardingStatus();

  const installationId = installationIdFromUrl || onboarding?.installationId;
  const onboardingRedirectUrl = `/onboarding?${new URLSearchParams({
    installation_id: installationId ?? '',
  }).toString()}`;
  const signInUrl = `/sign-in?${new URLSearchParams({
    redirect_url: onboardingRedirectUrl,
  }).toString()}`;

  const [paymentError, setPaymentError] = useState('');
  const [completeError, setCompleteError] = useState('');
  const [showRecovery, setShowRecovery] = useState(false);
  const [paymentMethodId, setPaymentMethodId] = useState<string | null>(null);
  const [couponCode, setCouponCode] = useState('');

  const { toast } = useToast();
  const { setActive } = useClerk();
  const { data: organizationsHealth, refetch: refetchHealth } =
    useOrganizationsWithHealth({
      enabled: !!isSignedIn,
    });
  const completeOnboarding = useCompleteOnboarding();
  const { data: pricing } = usePricing();
  // The GitHub App install goes active BEFORE Stripe runs in onboarding-complete, so an
  // active install with no subscription is the normal mid-onboarding state. Only treat a
  // user as already-onboarded (and bounce to the CLI page) once they ALSO have a
  // subscription; otherwise keep them here on the payment step.
  const { data: subscription } = useSubscription();
  const trialEndedAtLabel = formatTrialEnd(pricing?.trialEndedAt);

  const totalMembers = 1;
  // Show the canonical $30/seat immediately and let the API refine it (tiered
  // pricing). Falling back to the default avoids a blank price when the pricing
  // endpoint is slow or returns null (e.g. before the organization/subscription exists).
  const pricePerMember =
    pricing?.currentPricePerSeatCents ?? DEFAULT_SEAT_PRICE_CENTS;
  const totalPrice = totalMembers * pricePerMember;

  const isLoading = !isLoaded || isLoadingOnboarding;

  useEffect(() => {
    if (!isLoadingOnboarding && !installationId && !isSignedIn) {
      navigate({ to: '/' });
    }
  }, [isLoadingOnboarding, installationId, isSignedIn, navigate]);

  useEffect(() => {
    if (
      isLoaded &&
      isSignedIn &&
      onboarding &&
      onboarding.status === 'active' &&
      subscription != null
    ) {
      // Already onboarded (active install AND a subscription): send them to the CLI
      // download (TanStack Router v1 has no hash support, so use a raw location change
      // to carry the #download anchor).
      window.location.href = '/getting-started#download';
    }
  }, [isLoaded, isSignedIn, onboarding, subscription]);

  // recoverToHealthyOrganization switches the session to a healthy organization the user already
  // belongs to and routes to the dashboard. Returns false when no healthy organization is
  // available (so the caller can surface the manual recovery options instead).
  const recoverToHealthyOrganization = async (): Promise<boolean> => {
    try {
      const result = await refetchHealth();
      const organizations = result.data ?? organizationsHealth ?? [];
      const healthy = organizations.find(isHealthy);
      if (!healthy) return false;
      if (
        !(await setActiveOrganization(setActive, healthy.clerkOrganizationId))
      )
        return false;
      navigate({ to: '/dashboard' });
      return true;
    } catch {
      return false;
    }
  };

  const handleCompleteOnboarding = async () => {
    if (!installationId) return;

    setCompleteError('');
    setShowRecovery(false);
    try {
      const result = await completeOnboarding.mutateAsync({
        installationId,
        paymentMethodId: paymentMethodId || '',
        couponCode: couponCode || undefined,
      });
      // Switch the Clerk session to the just-created organization so its membership + admin role
      // land in the session JWT immediately. Without this the session keeps its prior token
      // (no organization) until Clerk's ~60s refresh, so a brand-new customer's dashboard renders with
      // no admin role and no billing for up to a minute after onboarding. Mirrors
      // recoverToHealthyOrganization's setActive on the recovery path.
      if (result.organizationId) {
        await setActiveOrganization(setActive, result.organizationId);
      }
      // Onboarding complete → send the now-active user to the CLI getting-started page
      // directly. This is deterministic: it does not depend on the onboarding-status query
      // invalidating/refetching to 'active' and the redirect effect winning a race against a
      // navigate() (which flaked). A raw location change carries the #download anchor that
      // TanStack Router v1 cannot (no hash support), matching the already-onboarded effect.
      window.location.href = '/getting-started#download';
    } catch (error) {
      // A 409 "already_linked" means this account already has an organization (e.g. a GitHub
      // reinstall race). Recover automatically by switching to a healthy organization the user
      // belongs to and going to the dashboard, instead of dead-ending on the error.
      if (
        error instanceof OnboardingError &&
        error.code === 'already_linked' &&
        (await recoverToHealthyOrganization())
      ) {
        return;
      }
      // Otherwise surface the failure (instead of silently leaving the form, which
      // reads as "nothing happened") and offer the manual recovery options so the
      // user is never dead-ended.
      const message =
        error instanceof Error
          ? error.message
          : 'Something went wrong starting your trial. Please try again.';
      setCompleteError(message);
      setShowRecovery(true);
      toast({
        title: 'Could not start your trial',
        description: message,
      });
    }
  };

  if (isLoaded && !isSignedIn) {
    return (
      <>
        <OnboardingNavigation />
        <main className="pt-16">
          <div className="bg-background min-h-screen">
            <section className="px-6 pt-12 pb-6 md:px-16">
              <div
                className="mx-auto flex justify-center"
                style={{ maxWidth: theme.spacing.maxWidth }}
              >
                <SignUp
                  routing="hash"
                  signInUrl={signInUrl}
                  forceRedirectUrl={onboardingRedirectUrl}
                  appearance={{
                    baseTheme: shadcn,
                    variables: {
                      colorPrimary: '#8b5cf6',
                    },
                    elements: {
                      formButtonPrimary: {
                        color: 'white',
                      },
                    },
                  }}
                />
              </div>
            </section>
          </div>
        </main>
        <Footer minimal />
        <ThemeToggle />
        <Toaster />
      </>
    );
  }

  // An already-onboarded user is redirected to /getting-started by the effect above.
  // Keep the skeleton up until that fires instead of flashing the fully-rendered form
  // for a frame.
  const isRedirectingOnboarded =
    isSignedIn && onboarding?.status === 'active' && subscription != null;

  if (isLoading || isRedirectingOnboarded) {
    return (
      <>
        <OnboardingNavigation />
        <main className="pt-16">
          <div className="bg-background min-h-screen">
            <section className="px-6 pt-16 pb-8 md:px-16">
              <div className="mx-auto max-w-6xl">
                <div className="mb-10 text-center">
                  <Skeleton className="mx-auto mb-6 h-12 w-[60%] max-w-[600px]" />
                  <Skeleton className="mx-auto h-6 w-[40%] max-w-[400px]" />
                </div>
                <div className="grid gap-8 md:grid-cols-2 md:items-start">
                  {/* Left column — value points */}
                  <div className="space-y-3">
                    <Skeleton className="mb-4 h-7 w-3/4" />
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Skeleton key={i} className="h-5 w-full" />
                    ))}
                    <Skeleton className="mt-6 h-4 w-2/3" />
                  </div>
                  {/* Right column — pricing + payment card */}
                  <Card>
                    <CardHeader>
                      <Skeleton className="h-8 w-40" />
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <Skeleton className="h-24 w-full" />
                        <Skeleton className="h-32 w-full" />
                        <Skeleton className="h-12 w-full" />
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </section>
          </div>
        </main>
        <Footer minimal />
        <ThemeToggle />
        <Toaster />
      </>
    );
  }

  return (
    <>
      <OnboardingNavigation />
      <main className="pt-16">
        <div className="bg-background min-h-screen">
          <section className="px-6 pt-12 pb-8 md:px-16">
            <div className="mx-auto max-w-6xl">
              <div className="mb-10 text-center">
                <h1
                  className="text-gradient-hero mb-4 text-4xl leading-tight font-bold tracking-tight md:text-5xl"
                  style={{ fontFamily: theme.fonts.display }}
                >
                  Judgment enhances generation.
                </h1>
                <p
                  className="text-lg text-white"
                  style={{ fontFamily: theme.fonts.body }}
                >
                  Welcome to Mindrealm! Let&apos;s ship better code, faster.
                </p>
              </div>

              <div className="grid gap-8 md:grid-cols-2 md:items-start">
                {/* Left column — what you're setting up (terse, from FAQ + pricing copy) */}
                <div style={{ fontFamily: theme.fonts.body }}>
                  <h2 className="mb-4 text-xl font-bold md:text-2xl">
                    Deterministic review that gets findings fixed
                  </h2>
                  <ul className="space-y-3">
                    {VALUE_POINTS.map((point) => (
                      <li key={point} className="flex items-start gap-3">
                        <span className="mt-1" style={{ color: '#8b5cf6' }}>
                          ✓
                        </span>
                        <span className="text-foreground/80 text-sm md:text-base">
                          {point}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-foreground/60 mt-6 text-sm">
                    A seat is one developer. Add or remove seats anytime —
                    billing adjusts automatically.
                  </p>
                </div>

                {/* Right column — pricing + payment */}
                <Card>
                  <CardHeader>
                    <h2 className="text-2xl font-bold">
                      Start your free trial
                    </h2>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="space-y-3">
                        <div className="flex justify-between text-base">
                          <span className="text-muted-foreground">
                            Price per seat:
                          </span>
                          <span className="font-semibold">
                            ${(pricePerMember / 100).toFixed(2)}/mo
                          </span>
                        </div>
                        <div className="flex justify-between text-base">
                          <span className="text-muted-foreground">
                            Total Members:
                          </span>
                          <span className="font-semibold">{totalMembers}</span>
                        </div>
                        <div className="border-border flex justify-between border-t pt-3 text-lg font-bold">
                          <span>Total today:</span>
                          <span>$0.00</span>
                        </div>
                        <div className="text-muted-foreground flex justify-between text-sm">
                          <span>After trial:</span>
                          <span>${(totalPrice / 100).toFixed(2)}/mo</span>
                        </div>
                      </div>

                      <div className="border-border rounded-md border border-dashed p-3 text-sm">
                        <span className="font-semibold text-green-500">
                          {TRIAL_DAYS}-day free trial.
                        </span>{' '}
                        <span className="text-foreground/70">
                          {trialEndedAtLabel
                            ? `You won't be charged until ${trialEndedAtLabel}. Cancel anytime before then and you pay nothing.`
                            : `You won't be charged during the trial. Cancel anytime.`}
                        </span>
                      </div>

                      <div className="border-border border-t pt-4">
                        {paymentMethodId ? (
                          <div className="flex items-center gap-2 text-green-500">
                            <span>✓</span>
                            <span>Payment method saved</span>
                          </div>
                        ) : (
                          <Elements
                            stripe={stripePromise}
                            options={{
                              appearance: { theme: 'night' },
                            }}
                          >
                            <PaymentForm
                              onSuccess={setPaymentMethodId}
                              onError={setPaymentError}
                              couponCode={couponCode}
                              onCouponCodeChange={setCouponCode}
                            />
                          </Elements>
                        )}
                        {paymentError && (
                          <p className="mt-2 text-sm text-red-500">
                            {paymentError}
                          </p>
                        )}
                      </div>

                      <Button
                        onClick={handleCompleteOnboarding}
                        disabled={
                          !paymentMethodId || completeOnboarding.isPending
                        }
                        className="w-full px-6 py-3 text-base"
                      >
                        {completeOnboarding.isPending
                          ? 'Starting your trial…'
                          : 'Start Free 7-Day Trial'}
                      </Button>
                      {completeError && (
                        <p className="text-sm text-red-500">{completeError}</p>
                      )}
                      {showRecovery && (
                        <div className="space-y-2">
                          <p className="text-foreground/70 text-sm">
                            Already set up? Go to your dashboard, or reconnect
                            the GitHub App if its access was removed.
                          </p>
                          <div className="flex flex-col gap-2 sm:flex-row">
                            <Button
                              variant="outline"
                              className="w-full"
                              onClick={() => navigate({ to: '/dashboard' })}
                            >
                              Go to dashboard
                            </Button>
                            <a href={GITHUB_APP_INSTALL_URL} className="w-full">
                              <Button variant="outline" className="w-full">
                                Reconnect GitHub App
                              </Button>
                            </a>
                          </div>
                        </div>
                      )}

                      <p className="text-foreground/50 text-center text-xs">
                        By starting your trial, you agree to our{' '}
                        <a href="/terms" className="underline">
                          Terms
                        </a>{' '}
                        and{' '}
                        <a href="/privacy" className="underline">
                          Privacy Policy
                        </a>
                        .
                      </p>
                      <p className="text-foreground/50 text-center text-xs">
                        Questions?{' '}
                        <a href="/contact" className="underline">
                          Contact us
                        </a>
                        .
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer minimal />
      <ThemeToggle />
      <Toaster />
    </>
  );
}

function OnboardingPage() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <OnboardingContent />
    </Suspense>
  );
}
